import { DownOutlined } from "@ant-design/icons"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Button, Checkbox, Dropdown, Input, message, Modal, Skeleton, Switch } from "antd"
import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { Link } from "react-router-dom"
import { ContainerFull } from "../components"
import { PLATE_STATUS_COLORS, ShowMessagePage } from "../config"
import { createWell, getPlate, getSample, updatePlateNotes, updatePlateStatus, updateWellPriority, updateWellSample } from "../endpoints"
import type { SampleInterfaceWithWellIdx, WellInterface } from "../types"

const ROWS = 8
const COLS = 12
const WELL_LABELS = Array.from({ length: ROWS }, (_, i) => String.fromCharCode("A".charCodeAt(0) + i))

type WellClipboard = {
    samples: (SampleInterfaceWithWellIdx | undefined)[]
    wellidxs: number[]
} | null

type PlateAction =
    | { type: "update"; wellId: number; prevSampleId: string | number | null; newSampleId: string | number | null }
    | { type: "create"; wellidx: number; sampleId: string | number | null }
    | { type: "delete"; wellId: number; prevSampleId: string | number | null }

export function PlateDetails(): JSX.Element {
    const { plateId } = useParams<{ plateId: string }>()

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getplate", plateId],
        queryFn: () => getPlate({ plateId: parseInt(plateId ?? "0") }),
        refetchOnMount: "always",
    })

    const [editingWell, setEditingWell] = useState<WellInterface | null>(null)
    const [newSampleId, setNewSampleId] = useState<string>("")
    const [newPriority, setNewPriority] = useState<string>("")
    const [modalOpen, setModalOpen] = useState(false)
    const [sampleInfo, setSampleInfo] = useState<any | null>(null)
    const [loadingSample, setLoadingSample] = useState(false)
    const [sampleError, setSampleError] = useState<string | null>(null)
    const [messageApi, contextHolder] = message.useMessage()
    const [clipboard, setClipboard] = useState<WellClipboard>(null)
    const [selectedWellIdxs, setSelectedWellIdxs] = useState<number[]>([]) // support multi-select
    const [actionHistory, setActionHistory] = useState<PlateAction[]>([])
    const [showWellId, setShowWellId] = useState(false) // checkbox state for showing well index
    const [showWellDbId, setShowWellDbId] = useState(false) // checkbox state for showing well database IDs
    const [showOrderId, setShowOrderId] = useState(false) // checkbox state for showing order IDs
    const [showSampleName, setShowSampleName] = useState(false) // checkbox state for showing sample names
    const [showSampleId, setShowSampleId] = useState(false) // checkbox state for showing sample IDs
    const [showSampleNum, setShowSampleNum] = useState(false) // checkbox state for showing sample numbers
    const [showTotalReads, setShowTotalReads] = useState(true) // checkbox state for showing total reads
    const [showUsedReads, setShowUsedReads] = useState(false) // checkbox state for showing used reads
    const [showPriority, setShowPriority] = useState(false) // checkbox state for showing priority
    const [showLabels, setShowLabels] = useState(false) // toggle to show/hide field labels inside wells
    const [plateNotes, setPlateNotes] = useState("")
    const [savingNotes, setSavingNotes] = useState(false)

    // Helper to push an action to history
    const pushAction = (action: PlateAction) => setActionHistory((prev) => [...prev, action])

    const handleClick = (well: WellInterface, e?: React.MouseEvent) => {
        if (!e) {
            setSelectedWellIdxs([well.wellidx])
            return
        }
        if (e.shiftKey && selectedWellIdxs.length > 0) {
            // Rectangular selection in column-major order
            const anchor = selectedWellIdxs[0]
            const anchorCol = Math.floor(anchor / ROWS)
            const anchorRow = anchor % ROWS
            const targetCol = Math.floor(well.wellidx / ROWS)
            const targetRow = well.wellidx % ROWS

            const minCol = Math.min(anchorCol, targetCol)
            const maxCol = Math.max(anchorCol, targetCol)
            const minRow = Math.min(anchorRow, targetRow)
            const maxRow = Math.max(anchorRow, targetRow)

            const newSelection: number[] = []
            for (let col = minCol; col <= maxCol; col++) {
                for (let row = minRow; row <= maxRow; row++) {
                    newSelection.push(col * ROWS + row)
                }
            }
            setSelectedWellIdxs(newSelection)
        } else if (e.ctrlKey || e.metaKey) {
            // Toggle selection
            if (selectedWellIdxs.includes(well.wellidx)) {
                setSelectedWellIdxs(selectedWellIdxs.filter((idx) => idx !== well.wellidx))
            } else {
                setSelectedWellIdxs([...selectedWellIdxs, well.wellidx])
            }
        } else {
            // Single select
            setSelectedWellIdxs([well.wellidx])
        }
    }

    const handleGetSampleInfo = async () => {
        setLoadingSample(true)
        setSampleError(null)
        setSampleInfo(null)
        try {
            const res = await getSample({ sampleId: newSampleId })
            if (res.code === 1000 && res.sample) {
                setSampleInfo(res.sample)
            } else {
                setSampleError(res.message || "Sample not found")
            }
        } catch (e: any) {
            setSampleError(e?.message || "Error fetching sample")
        }
        setLoadingSample(false)
    }

    const handleDoubleClick = (well: WellInterface | undefined, e?: React.MouseEvent) => {
        if (!well) return
        // Support right-click context menu for copy/cut/paste/delete

        // Default: open modal for editing
        setEditingWell(well)
        setNewSampleId("")
        setNewPriority(String(well.priority ?? 0))
        setModalOpen(true)
        setSampleInfo(null)
    }

    // Handler for saving the new sample ID (replace with your API call as needed)
    const handleSaveSampleId = async () => {
        if (!editingWell) return

        if (editingWell.id === -1) {
            // This is an empty well, so create it
            console.log("Creating new well with sample ID:", newSampleId)
            const res = await createWell({
                plateId: editingWell.plateId,
                wellidx: editingWell.wellidx,
                sampleId: newSampleId ? newSampleId : null,
            })
            if (res.code !== 1000) {
                messageApi.error(`Failed to create well: ${res.message}`)
                return
            }
            // Update priority if non-zero after creating
            const priorityVal = parseFloat(newPriority) || 0
            if (priorityVal !== 0 && res.wellId) {
                await updateWellPriority({ wellId: res.wellId, priority: priorityVal })
            }
            messageApi.success({
                content: `Sample for well ${editingWell
                        ? `${String.fromCharCode(65 + (editingWell.wellidx % ROWS))}${Math.floor(editingWell.wellidx / ROWS) + 1
                        }`
                        : ""
                    } set to ID ${newSampleId || "empty"}`,
            })
        } else {
            // Existing well: only update sample if user entered a new ID
            if (newSampleId) {
                const res = await updateWellSample({
                    wellId: editingWell.id,
                    sampleId: newSampleId,
                })
                if (res.code !== 1000) {
                    messageApi.error(`Failed to update sample: ${res.message}`)
                    return
                }
            }
            // Update priority if changed
            const priorityVal = parseFloat(newPriority) || 0
            if (priorityVal !== (editingWell.priority ?? 0)) {
                const res = await updateWellPriority({ wellId: editingWell.id, priority: priorityVal })
                if (res.code !== 1000) {
                    messageApi.error(`Failed to update priority: ${res.message}`)
                    return
                }
            }
            messageApi.success({
                content: `Well ${editingWell
                        ? `${String.fromCharCode(65 + (editingWell.wellidx % ROWS))}${Math.floor(editingWell.wellidx / ROWS) + 1
                        }`
                        : ""
                    } updated`,
            })
        }
        setModalOpen(false)
        setEditingWell(null)
        setNewSampleId("")
        setNewPriority("")
        setSampleInfo(null)
        queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
    }

    const plate = data?.plate
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't intercept keyboard shortcuts when the user is typing in an input/textarea
            const tag = (e.target as HTMLElement)?.tagName
            if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return
            if (selectedWellIdxs.length === 0) return
            if (plate) {
                const wells = plate.wells?.filter((w: WellInterface) => selectedWellIdxs.includes(w.wellidx)) || []
                if (e.ctrlKey || e.metaKey) {
                    if (e.key === "c") {
                        // Copy
                        const selectedWells =
                            plate.wells?.filter((w: WellInterface) => selectedWellIdxs.includes(w.wellidx)) || []
                        setClipboard({
                            samples: selectedWells.map((w) => w.sample as SampleInterfaceWithWellIdx | undefined),
                            wellidxs: selectedWells.map((w) => w.wellidx),
                        })
                        messageApi.success({ content: "Wells copied" })
                    } else if (e.key === "x") {
                        // Cut
                        const selectedWells =
                            plate.wells?.filter((w: WellInterface) => selectedWellIdxs.includes(w.wellidx)) || []
                        setClipboard({
                            samples: selectedWells.map((w) => w.sample as SampleInterfaceWithWellIdx | undefined),
                            wellidxs: selectedWells.map((w) => w.wellidx),
                        })
                        wells.forEach((well) => {
                            if (well.id !== -1 && well.sample) {
                                // Save previous sampleId for undo
                                pushAction({
                                    type: "update",
                                    wellId: well.id,
                                    prevSampleId: well.sample.id ?? null,
                                    newSampleId: null,
                                })
                                updateWellSample({ wellId: well.id, sampleId: null }).then((res) => {
                                    if (res.code === 1000) {
                                        messageApi.success({ content: "Well cut" })
                                        queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
                                    } else {
                                        messageApi.error(res.message || "Failed to cut sample")
                                    }
                                })
                            }
                        })
                    } else if (e.key === "v") {
                        // Paste
                        if (clipboard && clipboard.samples.length > 0) {
                            // Find the starting index
                            const startIdx = selectedWellIdxs.length > 0 ? Math.min(...selectedWellIdxs) : 0 // or another sensible default if selectedWellIdx is not defined
                            if (startIdx == null) return

                            clipboard.samples.forEach((sample, i) => {
                                const targetIdx = startIdx + i // or use your plate layout logic for next well
                                const well = plate.wells?.find((w: WellInterface) => w.wellidx === targetIdx) || {
                                    id: -1,
                                    label: "",
                                    wellidx: targetIdx,
                                    plateId: plate.id,
                                    sample: undefined,
                                    ts: new Date().toISOString(),
                                }
                                const sampleId = sample?.id ?? null
                                if (well.id === -1) {
                                    // Create new well
                                    pushAction({ type: "create", wellidx: well.wellidx, sampleId })
                                    createWell({
                                        plateId: plate.id,
                                        wellidx: well.wellidx,
                                        sampleId,
                                    }).then((res) => {
                                        if (res.code === 1000) {
                                            queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
                                        }
                                    })
                                } else {
                                    // Save previous sampleId for undo
                                    pushAction({
                                        type: "update",
                                        wellId: well.id,
                                        prevSampleId: well.sample?.id ?? null,
                                        newSampleId: sampleId,
                                    })
                                    updateWellSample({
                                        wellId: well.id,
                                        sampleId,
                                    }).then((res) => {
                                        if (res.code === 1000) {
                                            queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
                                        }
                                    })
                                }
                            })
                            messageApi.success({ content: "Samples pasted" })
                        } else {
                            messageApi.info({ content: "Clipboard is empty" })
                        }
                    }
                } else if (e.key === "Delete" || e.key === "Backspace") {
                    // Delete sample from all selected wells
                    wells.forEach((well) => {
                        if (well.id !== -1 && well.sample) {
                            pushAction({
                                type: "update",
                                wellId: well.id,
                                prevSampleId: well.sample.id ?? null,
                                newSampleId: null,
                            })
                            updateWellSample({ wellId: well.id, sampleId: null }).then((res) => {
                                if (res.code === 1000) {
                                    messageApi.success({ content: "Sample deleted" })
                                    queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
                                } else {
                                    messageApi.error(res.message || "Failed to delete sample")
                                }
                            })
                        }
                    })
                }
            }
        }
        window.addEventListener("keydown", handleKeyDown)
        return () => window.removeEventListener("keydown", handleKeyDown)
    }, [selectedWellIdxs, clipboard, plate, messageApi, queryClient, plateId])

    useEffect(() => {
        const handleUndo = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName
            if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                if (plate) {
                    if (actionHistory.length === 0) return
                    const lastAction = actionHistory[actionHistory.length - 1]
                    if (lastAction.type === "update") {
                        updateWellSample({
                            wellId: lastAction.wellId,
                            sampleId: lastAction.prevSampleId ?? null,
                        }).then((res) => {
                            if (res.code === 1000) {
                                messageApi.success({ content: "Undo successful" })
                                queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
                            } else {
                                messageApi.error(res.message || "Undo failed")
                            }
                        })
                    } else if (lastAction.type === "create") {
                        // Remove the well by setting sampleId to null (if possible)
                        // Or, if you have a deleteWell endpoint, call it here
                        // For now, just clear the sample
                        const well = plate.wells?.find((w: WellInterface) => w.wellidx === lastAction.wellidx)
                        if (well && well.id !== -1) {
                            updateWellSample({
                                wellId: well.id,
                                sampleId: null,
                            }).then((res) => {
                                if (res.code === 1000) {
                                    messageApi.success({ content: "Undo create successful" })
                                    queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
                                } else {
                                    messageApi.error(res.message || "Undo failed")
                                }
                            })
                        }
                    }
                    // Remove the last action from history
                    setActionHistory((prev) => prev.slice(0, -1))
                }
            }
        }
        window.addEventListener("keydown", handleUndo)
        return () => window.removeEventListener("keydown", handleUndo)
    }, [actionHistory, plate, messageApi, queryClient, plateId])

    // Add this handler for updating plate status
    const handlePlateStatusChange = async (info: any) => {
        const key = info.key
        if (!plate) return
        if (key === plate.status) return
        const res = await updatePlateStatus({ plateId: plate.id, newStatus: key })
        if (res.code === 1000) {
            messageApi.success({ content: `Plate status updated to ${key}` })
            queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
        } else {
            messageApi.error(res.message || "Failed to update plate status")
        }
    }

    const handleSavePlateNotes = async () => {
        if (!plate) return
        setSavingNotes(true)
        const res = await updatePlateNotes({ plateId: plate.id, notes: plateNotes })
        setSavingNotes(false)
        if (res.code === 1000) {
            messageApi.success({ content: "Plate notes updated" })
            queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
        } else {
            messageApi.error(res.message || "Failed to update plate notes")
        }
    }

    useEffect(() => {
        setPlateNotes(plate?.notes || "")
    }, [plate?.notes])

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }

    if (data.code !== 1000) {
        return ShowMessagePage("Error", data.message)
    }
    if (!plate) return <div>No plate found in database.</div>

    const totalReadsSum = (plate.wells || []).reduce((sum: number, w: WellInterface) => sum + (w.totalreads ?? 0), 0)

    const wellsWithSamples = (plate.wells || []).filter((w: WellInterface) => !!w.sample)
    const samplesWithoutOrderId = wellsWithSamples.filter((w: WellInterface) => !w.sample?.orderId)
    const samplesWithOrderId = wellsWithSamples.filter((w: WellInterface) => !!w.sample?.orderId)
    const hasSequence = (w: WellInterface) => {
        const hasSequenceString = (w.sample?.sequence ?? "").trim().length > 0
        const hasReads = (w.totalreads ?? 0) > 0 || (w.usedreads ?? 0) > 0
        return hasSequenceString || hasReads
    }
    const samplesWithSequence = samplesWithOrderId.filter((w: WellInterface) => hasSequence(w))
    const samplesWithOutSequence = samplesWithOrderId.filter((w: WellInterface) => !hasSequence(w))
    // console.log("Plate details:", plate)
    // console.log("Wells with totalreads:", plate.wells?.filter((w: WellInterface) => w.totalreads != null))
    console.log("Wells with samples:", wellsWithSamples)
    console.log("plate:", plate)
    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            {contextHolder}
            <h2>
                Plate #{plate.id} ({plate.type})
            </h2>
            <Button type="link" style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>
                {"<< Back"}
            </Button>
            {/* Plate status dropdown */}
            <div style={{ marginBottom: 16 }}>
                <b>Status: </b>
                <Dropdown
                    menu={{
                        items: Object.entries(PLATE_STATUS_COLORS).map(([status, color]) => ({
                            key: status,
                            label: (
                                <span
                                    style={{
                                        display: "block",
                                        backgroundColor: color,
                                        color: "#222",
                                        padding: "4px 12px",
                                        width: "100%",
                                    }}
                                >
                                    {status || "Unknown"}
                                </span>
                            ),
                            style: { padding: 0 },
                        })),
                        onClick: handlePlateStatusChange,
                    }}
                    trigger={["click"]}
                >
                    <Button
                        size="small"
                        style={{
                            backgroundColor: PLATE_STATUS_COLORS[plate.status ?? ""] || "#eee",
                            color: "#222",
                            minWidth: 120,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                        }}
                    >
                        <span
                            style={{
                                flex: 1,
                                textAlign: "center",
                                pointerEvents: "none",
                                marginRight: 24,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {plate.status ?? ""}
                        </span>
                        <span
                            style={{
                                position: "absolute",
                                right: 8,
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                            }}
                        >
                            <DownOutlined />
                        </span>
                    </Button>
                </Dropdown>
            </div>
            <div style={{ marginBottom: 16 }}>
                <b>Creator:</b> {plate.creator?.firstName} {plate.creator?.lastName}
            </div>
            <div style={{ marginBottom: 16 }}>
                <b>Created:</b>
                <span> {new Date(plate.ts).toLocaleString()}</span>
            </div>
            <div style={{ marginBottom: 16 }}>
                <b>Notes:</b>
                <div style={{ marginTop: 8, maxWidth: 700 }}>
                    <Input.TextArea
                        value={plateNotes}
                        onChange={(e) => setPlateNotes(e.target.value)}
                        placeholder="Enter notes for this plate"
                        autoSize={{ minRows: 2, maxRows: 6 }}
                    />
                    <div style={{ marginTop: 8 }}>
                        <Button
                            type="primary"
                            size="small"
                            onClick={handleSavePlateNotes}
                            loading={savingNotes}
                            disabled={plateNotes === (plate.notes || "")}
                        >
                            Save Notes
                        </Button>
                    </div>
                </div>
            </div>
            <div style={{ marginBottom: 16 }}>
                <b>Run ID:</b>{" "}
                {plate.runId ? (
                    <Link to={`/admin/run/${plate.runId}`}>{plate.runId}</Link>
                ) : (
                    <span style={{ color: "#bbb" }}>N/A</span>
                )}
            </div>

            <div style={{ marginBottom: 16 }}>
                <b>Orders in this plate:</b>{" "}
                {Array.from(
                    new Set(
                        (plate.wells || [])
                            .map((w: WellInterface) => w.sample?.orderId)
                            .filter((orderId): orderId is number => !!orderId)
                    )
                ).map((orderId, i, arr) => (
                    <span key={orderId}>
                        <Link to={`/admin/order/${orderId}`}>{orderId}</Link>
                        {i < arr.length - 1 && ", "}
                    </span>
                ))}
                {(!plate.wells || plate.wells.length === 0) && <span style={{ color: "#bbb" }}>None</span>}
            </div>
            <div style={{ marginBottom: 16 }}>
                <b>n samples:</b> {wellsWithSamples.length} &nbsp;&nbsp;&nbsp;&nbsp;
                <b>with sequence:</b> {samplesWithSequence.length} &nbsp;&nbsp;&nbsp;&nbsp;
                <b>without sequence:</b> {samplesWithOutSequence.length} &nbsp;&nbsp;&nbsp;&nbsp;
                <b>other:</b> {samplesWithoutOrderId.length}
            </div>

            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <Checkbox
                        checked={showSampleName}
                        onChange={(e) => setShowSampleName(e.target.checked)}
                    >
                        Show Sample Name
                    </Checkbox>
                    <Checkbox
                        checked={showWellId}
                        onChange={(e) => setShowWellId(e.target.checked)}
                    >
                        Show well index (barcode)
                    </Checkbox>

                    <Checkbox
                        checked={showOrderId}
                        onChange={(e) => setShowOrderId(e.target.checked)}
                    >
                        Show Order ID
                    </Checkbox>


                    <Checkbox
                        checked={showSampleNum}
                        onChange={(e) => setShowSampleNum(e.target.checked)}
                    >
                        Show samplenum
                    </Checkbox>
                    <Checkbox
                        checked={showTotalReads}
                        onChange={(e) => setShowTotalReads(e.target.checked)}
                    >
                        Show total reads
                    </Checkbox>
                    <Checkbox
                        checked={showUsedReads}
                        onChange={(e) => setShowUsedReads(e.target.checked)}
                    >
                        Show used reads
                    </Checkbox>
                    <Checkbox
                        checked={showWellDbId}
                        onChange={(e) => setShowWellDbId(e.target.checked)}
                    >
                        Show well DB ID
                    </Checkbox>
                    <Checkbox
                        checked={showSampleId}
                        onChange={(e) => setShowSampleId(e.target.checked)}
                    >
                        Show Sample ID
                    </Checkbox>
                    <Checkbox
                        checked={showPriority}
                        onChange={(e) => setShowPriority(e.target.checked)}
                    >
                        Show Priority
                    </Checkbox>
                </div>

            </div>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <Button
                        onClick={() => {
                            setShowWellId(false)
                            setShowWellDbId(false)
                            setShowOrderId(false)
                            setShowSampleName(false)
                            setShowSampleId(false)
                            setShowSampleNum(false)
                            setShowTotalReads(false)
                            setShowUsedReads(false)
                            setShowPriority(false)
                        }}
                        size="small"
                    >
                        Clear Selections
                    </Button>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Switch size="small" checked={showLabels} onChange={setShowLabels} />
                        <span style={{ fontSize: "0.9em", color: "#555" }}>Show labels</span>
                    </div>
                </div>
            </div>

            <div style={{ marginBottom: 8, color: "#888", fontSize: "0.9em" }}>
                Note: Double-click a well to open the edit dialog. Click to select, Shift+Click for range, Ctrl/Cmd+Click to multi-select.
            </div>

            {/* Well information guide */}
            <div style={{ marginBottom: 12, display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
                {/* Color legend */}
                <div>
                    <div style={{ fontSize: "0.85em", fontWeight: 600, color: "#555", marginBottom: 4 }}>Well colors</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: "0.82em" }}>
                        {[
                            { color: "#ffe58f", label: "Selected" },
                            { color: "#9be5a5", label: "Has sequence" },
                            { color: "#e6f7ff", label: "Has order, no sequence" },
                            { color: "#dce29d", label: "Sample, no order" },
                            { color: "#fafafa", label: "Empty", border: "1px solid #ccc" },
                        ].map(({ color, label, border }) => (
                            <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <div style={{ width: 16, height: 16, background: color, border: border ?? "1px solid #ccc", borderRadius: 2, flexShrink: 0 }} />
                                <span style={{ color: "#555" }}>{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Fields shown in wells */}
                {(showSampleName || showSampleId || showSampleNum || showOrderId || showWellId || showWellDbId || showTotalReads || showUsedReads || showPriority) && (
                    <div>
                        <div style={{ fontSize: "0.85em", fontWeight: 600, color: "#555", marginBottom: 4 }}>Well preview</div>
                        <div style={{
                            display: "inline-block",
                            border: "1px solid #ccc",
                            padding: 6,
                            background: "#e6f7ff",
                            minWidth: 80,
                            textAlign: "center",
                            fontSize: "0.85em",
                            verticalAlign: "middle",
                        }}>
                            {showSampleName && <div><b>Sample name</b></div>}
                            {showSampleId && <div style={{ fontSize: "8pt", color: "#666" }}>Sample DB ID: 1234</div>}
                            {showSampleNum && <div style={{ fontSize: "8pt", color: "#666" }}>samplenum:56</div>}
                            {showOrderId && <div style={{ fontSize: "8pt" }}>Order: 789</div>}
                            {showWellId && <div style={{ fontSize: "8pt", color: "#666" }}>Barcode:1</div>}
                            {showWellDbId && <div style={{ fontSize: "8pt", color: "#666" }}>Well DB ID: 42</div>}
                            {showTotalReads && <div style={{ fontSize: "8pt", color: "#888" }}>Total reads: 1,000,000</div>}
                            {showUsedReads && <div style={{ fontSize: "8pt", color: "#666" }}>Used Reads: 800,000</div>}
                            {showPriority && <div style={{ fontSize: "8pt", color: "#888" }}>Priority: 5</div>}
                        </div>
                    </div>
                )}
            </div>

            {showTotalReads && (
                <div style={{ marginBottom: 8, fontSize: "0.95em" }}>
                    <b>Total reads (all wells):</b> {totalReadsSum.toLocaleString()}
                </div>
            )}

            <div id="plate-view-area" style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", minWidth: 900 }}>
                    <thead>
                        <tr>
                            <th></th>
                            {Array.from({ length: COLS }, (_, i) => (
                                <th key={i} style={{ border: "1px solid #ccc", padding: 6 }}>
                                    {i + 1}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {WELL_LABELS.map((rowLabel, rowIdx) => (
                            <tr key={rowLabel}>
                                <td style={{ border: "1px solid #ccc", padding: 6, fontWeight: "bold" }}>{rowLabel}</td>
                                {Array.from({ length: COLS }, (_, colIdx) => {
                                    const idx = colIdx * ROWS + rowIdx
                                    // Try to find the well, or create a placeholder if not found
                                    let well: WellInterface = plate.wells?.find((w: WellInterface) => w.wellidx === idx) || {
                                        id: -1, // or 0, or undefined if your backend allows
                                        label: `${WELL_LABELS[rowIdx]}${colIdx + 1}`,
                                        wellidx: idx,
                                        plateId: plate.id,
                                        sample: undefined,
                                        ts: new Date().toISOString(),
                                    }
                                    const sample = well.sample as SampleInterfaceWithWellIdx | undefined
                                    return (
                                        <td
                                            key={colIdx}
                                            style={{
                                                border: "1px solid #ccc",
                                                padding: 6,
                                                background: selectedWellIdxs.includes(well.wellidx)
                                                    ? "#ffe58f" // highlight color for selected well
                                                    : sample
                                                        ? sample.sequence
                                                            ? "#9be5a5"
                                                            : sample.orderId
                                                                ? "#e6f7ff"
                                                                : "#dce29d"
                                                        : "#fafafa",
                                                minWidth: 80,
                                                textAlign: "center",
                                                fontSize: "0.95em",
                                                height: 40,
                                                verticalAlign: "middle",
                                                cursor: "pointer",
                                            }}
                                            onClick={(e) => handleClick(well, e)}
                                            onDoubleClick={() => handleDoubleClick(well)}
                                        >
                                            {sample ? (
                                                <span>
                                                    {showSampleName && <b>{sample.sampleName}</b>}
                                                    {showSampleId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt", color: "#666" }}>
                                                                {showLabels ? "Sample DB ID: " : ""}{sample.id ?? ""}
                                                            </span>
                                                        </>
                                                    )}
                                                    {showSampleNum && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt", color: "#666" }}>
                                                                {showLabels ? "samplenum:" : "#"}{sample.samplenum ?? ""}
                                                            </span>
                                                        </>
                                                    )}
                                                    {showOrderId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt" }}>{showLabels ? "Order: " : ""}{sample.orderId || ""}</span>
                                                        </>
                                                    )}

                                                    {showWellId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt", color: "#666" }}>
                                                                {showLabels ? "Barcode:" : ""}{well.wellidx + 1}
                                                            </span>
                                                        </>
                                                    )}
                                                    {showWellDbId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt", color: "#666" }}>
                                                                {showLabels ? "Well DB ID: " : ""}{well.id}
                                                            </span>
                                                        </>
                                                    )}
                                                    {showTotalReads && well.totalreads != null && well.totalreads > 0 && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt", color: "#888" }}>
                                                                {showLabels ? "Total reads: " : ""}{well.totalreads.toLocaleString()}
                                                            </span>
                                                        </>
                                                    )}
                                                    {showUsedReads && well.usedreads != null && well.usedreads > 0 && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt", color: "#666" }}>
                                                                {showLabels ? "Used Reads: " : ""}{well.usedreads.toLocaleString()}
                                                            </span>
                                                        </>
                                                    )}
                                                    {showPriority && well.priority != null && well.priority !== 0 && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt", color: "#888" }}>
                                                                {showLabels ? "Priority: " : "P:"}{well.priority}
                                                            </span>
                                                        </>
                                                    )}
                                                </span>
                                            ) : (
                                                <span style={{ color: "#bbb" }}>
                                                    {showSampleName && <><b>—</b></>}
                                                    {showSampleId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt" }}>{showLabels ? "Sample DB ID: " : ""}—</span>
                                                        </>
                                                    )}
                                                    {showSampleNum && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt" }}>{showLabels ? "samplenum:" : "#"}—</span>
                                                        </>
                                                    )}
                                                    {showOrderId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt" }}>{showLabels ? "Order: " : ""}—</span>
                                                        </>
                                                    )}
                                                    {showWellId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt" }}>{showLabels ? "Barcode:" : ""}{well.wellidx + 1}</span>
                                                        </>
                                                    )}
                                                    {showWellDbId && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt" }}>{showLabels ? "Well DB ID: " : ""}{well.id === -1 ? "—" : well.id}</span>
                                                        </>
                                                    )}
                                                    {showPriority && (
                                                        <>
                                                            <br />
                                                            <span style={{ fontSize: "8pt" }}>{showLabels ? "Priority: " : "P:"}{well.priority ?? 0}</span>
                                                        </>
                                                    )}
                                                    {!showSampleName && !showSampleId && !showSampleNum && !showOrderId && !showWellId && !showWellDbId && !showTotalReads && !showUsedReads && !showPriority && "\u00A0"}
                                                </span>
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {plate.modifications && (
                <div
                    style={{
                        margin: "16px 0",
                        padding: "12px",
                        background: "#f6f6f6",
                        border: "1px solid #eee",
                        borderRadius: 4,

                        whiteSpace: "pre-line",
                    }}
                >
                    <b>Change History:</b>
                    <div>
                        {plate.modifications
                            .split("\n")
                            .filter((line) => line.trim().length > 0)
                            .map((line, idx) => (
                                <div key={idx}>{line}</div>
                            ))}
                    </div>
                </div>
            )}
            <Modal
                title={
                    editingWell
                        ? `Edit Well ${String.fromCharCode(65 + (editingWell.wellidx % ROWS))}${Math.floor(editingWell.wellidx / ROWS) + 1
                        }`
                        : "Edit Well"
                }
                open={modalOpen}
                onOk={handleSaveSampleId}
                onCancel={() => { setModalOpen(false); setNewPriority("") }}
                okText="Save"
                footer={[
                    <Button
                        key="get-sample"
                        style={{ marginTop: 8 }}
                        onClick={handleGetSampleInfo}
                        loading={loadingSample}
                        disabled={!newSampleId}
                    >
                        Get Sample
                    </Button>,
                    <Button
                        key="delete"
                        danger
                        disabled={!editingWell || editingWell.id === -1 || !editingWell.sample}
                        onClick={async () => {
                            if (!editingWell) return
                            pushAction({
                                type: "update",
                                wellId: editingWell.id,
                                prevSampleId: editingWell.sample?.id ?? null,
                                newSampleId: null,
                            })
                            const res = await updateWellSample({ wellId: editingWell.id, sampleId: null })
                            if (res.code !== 1000) {
                                messageApi.error(`Failed to remove sample: ${res.message}`)
                                return
                            }
                            messageApi.success({
                                content: `Sample removed from well ${editingWell
                                        ? `${String.fromCharCode(65 + (editingWell.wellidx % ROWS))}${Math.floor(editingWell.wellidx / ROWS) + 1
                                        }`
                                        : ""
                                    }`,
                            })
                            setModalOpen(false)
                            setEditingWell(null)
                            setNewSampleId("")
                            setNewPriority("")
                            setSampleInfo(null)
                            queryClient.invalidateQueries({ queryKey: ["getplate", plateId] })
                        }}
                    >
                        Delete Sample
                    </Button>,
                    <Button key="cancel" onClick={() => setModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveSampleId}>
                        Save
                    </Button>,
                ]}
            >
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <label>Current Sample ID:</label>
                        <span>{editingWell?.sample?.id || ""}</span>
                    </div>
                    <label>New Sample ID:</label>
                    <Input
                        value={newSampleId}
                        onChange={(e) => setNewSampleId(e.target.value)}
                        placeholder="Enter sample ID or leave blank to clear"
                        type="number"
                        allowClear
                    />
                    <div style={{ marginTop: 12 }}>
                        <label>Priority:</label>
                        <Input
                            value={newPriority}
                            onChange={(e) => setNewPriority(e.target.value)}
                            placeholder="0"
                            type="number"
                            style={{ marginTop: 4 }}
                        />
                    </div>
                    <div style={{ marginTop: 12, minHeight: 32 }}>
                        {sampleError && <span style={{ color: "red" }}>{sampleError}</span>}
                        {sampleInfo && (
                            <div style={{ color: "#333" }}>
                                <div>
                                    <b>Sample ID:</b> {sampleInfo.id}
                                </div>
                                <div>
                                    <b>Sample name:</b> {sampleInfo.sampleName}
                                </div>
                                <div>
                                    <b>Order ID:</b> {sampleInfo.orderId ?? <span style={{ color: "#bbb" }}>N/A</span>}
                                </div>
                                <div>
                                    <b>Sequence:</b>{" "}
                                    {sampleInfo.sequence ? (
                                        <span>{sampleInfo.sequence.length}bp</span>
                                    ) : (
                                        <span style={{ color: "#bbb" }}>N/A</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <div style={{ marginTop: 32, padding: "16px 20px", background: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: 6, maxWidth: 700 }}>
                <b style={{ fontSize: "1em" }}>How to edit wells</b>
                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", fontSize: "0.9em", color: "#444" }}>
                    <div><b>Selection</b></div>
                    <div></div>
                    <div>Click</div><div>Select a single well</div>
                    <div>Shift+Click</div><div>Rectangular range selection</div>
                    <div>Ctrl/Cmd+Click</div><div>Toggle individual wells</div>

                    <div style={{ marginTop: 8 }}><b>Editing</b></div>
                    <div></div>
                    <div>Double-click</div><div>Open edit dialog to assign or clear a sample</div>
                    <div>Delete / Backspace</div><div>Clear samples from selected wells</div>

                    <div style={{ marginTop: 8 }}><b>Copy &amp; Paste</b></div>
                    <div></div>
                    <div>Ctrl/Cmd+C</div><div>Copy selected wells</div>
                    <div>Ctrl/Cmd+X</div><div>Cut (copy and clear) selected wells</div>
                    <div>Ctrl/Cmd+V</div><div>Paste into wells starting at selection</div>
                    <div>Ctrl/Cmd+Z</div><div>Undo last action</div>
                </div>
            </div>
        </ContainerFull>
    )
}
